import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";
import { getBearerToken } from "./bearerToken";

export interface AuthLetterReviewDumpJCodeEntry {
  j_code: string;
  auth_status?: string | null;
  auth_validation_status?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  num_visits?: string | null;
  dosage?: string | null;
  drug_name?: string | null;
  generic_name?: string | null;
  units_approved?: string | null;
  human_validated?: boolean | null;
  basic_info_validated?: boolean | null;
  diagnosis_validated?: boolean | null;
}

export interface AuthLetterReviewDumpDiagnosisEntry {
  icd_code?: string;
  icd_description?: string;
  diagnosis?: string;
}

/**
 * Payload sent to submit user-reviewed auth-letter entries.
 *
 * Includes order/user context and per–J-code review values.
 */
export interface AuthLetterReviewDumpPayload {
  order_id: string;
  patient_id: string;
  org_id: string;
  user_id: string;
  payer_name: string;
  basic_info_validated?: boolean | null;
  diagnosis_validated?: boolean | null;
  auth_letter_path: string;
  member_id: string;
  provider: string;
  auth_number: string;
  auth_initiated_date?: string;
  source_portal?: string | null;
  primary_insurance_plan_name?: string;
  primary_diagnosis_details?: AuthLetterReviewDumpDiagnosisEntry[];
  j_code_entries: AuthLetterReviewDumpJCodeEntry[];
}

/**
 * Sends the auth letter review payload to the dump API (e.g. for ML/analytics).
 * Called before persisting the review to Firestore.
 */
export const postAuthLetterReviewDump = async (
  payload: AuthLetterReviewDumpPayload,
): Promise<void> => {
  const token = await getBearerToken();
  if (!token) {
    throw new Error("Bearer token not available");
  }

  const response = await fetch(API_ENDPOINTS.AUTH_LETTER_REVIEW_DUMP, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorText = await response.text();
    logError(
      new Error(
        `Auth letter review dump failed: ${response.status} ${response.statusText}`,
      ),
      errorText,
    );
    throw new Error(
      `Auth letter review dump failed: ${response.status} ${response.statusText}`,
    );
  }
};
